import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Modal,
  Stack,
  Group,
  Text,
  Button,
  TextInput,
  Textarea,
  Alert,
  Badge,
  Tooltip,
  Paper,
  Divider,
  Box,
  Checkbox,
  Loader,
  ScrollArea,
  UnstyledButton,
} from '@mantine/core';
import { AlertCircle, Eye, Tag, Mail, Info, AlertTriangle } from 'react-feather';
import { intakeApi, type IntakeRecipient } from '../../../services/api/intake';
import { notifications } from '@mantine/notifications';
import { useInsertAtCursor } from '../../../hooks';

// Merge tags available for reminder emails
const MERGE_TAGS = [
  { tag: '{{first_name}}', label: 'First Name', example: 'John' },
  { tag: '{{last_name}}', label: 'Last Name', example: 'Doe' },
  { tag: '{{cohort_name}}', label: 'Cohort Name', example: 'Q1 Leadership' },
  { tag: '{{form_link}}', label: 'Form Link', example: 'https://...' },
];

const DEFAULT_SUBJECT = 'Reminder: Please Complete Your Intake Form';
// The intake email template renders its own "Hi {first_name}," greeting above
// the body, so DEFAULT_BODY starts with the message content to avoid a
// duplicate greeting in the rendered email.
const DEFAULT_BODY = `We noticed you haven't completed your intake form for {{cohort_name}} yet.

This form helps us match you with the right coach and prepare for your sessions. It only takes a few minutes to complete!

Please click the link below:
{{form_link}}

If you have any questions or need assistance, please don't hesitate to reach out.

Best regards,
The Medley Team`;

interface SendReminderModalProps {
  opened: boolean;
  onClose: () => void;
  cohortId: string;
  onSent: () => void;
}

export function SendReminderModal({
  opened,
  onClose,
  cohortId,
  onSent,
}: SendReminderModalProps) {
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [body, setBody] = useState(DEFAULT_BODY);
  const [sending, setSending] = useState(false);
  const [confirmOpened, setConfirmOpened] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [lastFocusedField, setLastFocusedField] = useState<'subject' | 'body'>('body');
  const subjectField = useInsertAtCursor<HTMLInputElement>();
  const bodyField = useInsertAtCursor<HTMLTextAreaElement>();

  // Participant selection state
  const [recipients, setRecipients] = useState<IntakeRecipient[]>([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const fetchRecipients = useCallback(async () => {
    setLoadingRecipients(true);
    try {
      const data = await intakeApi.getRecipients(cohortId);
      setRecipients(data);
      // Pre-select all eligible (non-completed) participants
      const eligible = data.filter((r) => r.intake_status !== 'completed');
      setSelectedIds(new Set(eligible.map((r) => r.id)));
    } catch (error) {
      console.error('Failed to fetch recipients:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to load participants',
        color: 'red',
      });
    } finally {
      setLoadingRecipients(false);
    }
  }, [cohortId]);

  useEffect(() => {
    if (opened) {
      fetchRecipients();
    }
  }, [opened, fetchRecipients]);

  // Derived state
  const eligibleRecipients = useMemo(
    () => recipients.filter((r) => r.intake_status !== 'completed'),
    [recipients],
  );

  const completedRecipients = useMemo(
    () => recipients.filter((r) => r.intake_status === 'completed'),
    [recipients],
  );

  const selectedCount = selectedIds.size;

  const firstTimeSelected = useMemo(
    () => recipients.filter((r) => r.intake_status === 'not_configured' && selectedIds.has(r.id)),
    [recipients, selectedIds],
  );

  // Selection handlers
  const toggleParticipant = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectAll = () => {
    setSelectedIds(new Set(eligibleRecipients.map((r) => r.id)));
  };

  const deselectAll = () => {
    setSelectedIds(new Set());
  };

  const handleInsertTag = (tag: string) => {
    if (lastFocusedField === 'subject') {
      subjectField.insert(tag, () => subject, setSubject);
    } else {
      bodyField.insert(tag, () => body, setBody);
    }
  };

  const getPreviewText = (text: string) => {
    return text
      .replace(/\{\{first_name\}\}/g, 'John')
      .replace(/\{\{last_name\}\}/g, 'Doe')
      .replace(/\{\{full_name\}\}/g, 'John Doe')
      .replace(/\{\{cohort_name\}\}/g, 'Q1 Leadership')
      .replace(/\{\{form_link\}\}/g, 'https://withmedley.paperform.co/intake?...')
      .replace(/\{\{intake_form_link\}\}/g, 'https://withmedley.paperform.co/intake?...');
  };

  const handleSend = async () => {
    if (selectedCount === 0) return;

    setSending(true);
    try {
      const result = await intakeApi.sendReminders(cohortId, {
        subject: subject || DEFAULT_SUBJECT,
        body: body || DEFAULT_BODY,
        participant_ids: Array.from(selectedIds),
      });

      if (result.sent > 0) {
        onSent();
      } else if (result.skipped && result.skipped > 0) {
        notifications.show({
          title: 'Info',
          message: `${result.skipped} participant(s) skipped (max 3 reminders reached)`,
          color: 'blue',
        });
        onClose();
      } else {
        notifications.show({
          title: 'Info',
          message: 'No participants needed reminders',
          color: 'blue',
        });
        onClose();
      }
    } catch (error) {
      console.error('Failed to send reminders:', error);
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to send reminders',
        color: 'red',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <Mail size={18} />
          <Text fw={600}>Resend Intake Form</Text>
        </Group>
      }
      size="lg"
      centered
    >
      <Stack gap="md">
        <Alert color="blue" icon={<AlertCircle size={16} />}>
          <Text size="sm">
            This will send a reminder to participants who received the intake form but haven't
            completed it yet.
            <br />
            <Text size="xs" c="dimmed" mt={4}>
              Note: Each participant can receive a maximum of 3 reminder emails.
            </Text>
          </Text>
        </Alert>

        {/* Participant Selection */}
        <Box>
          <Group justify="space-between" mb={8}>
            <Text size="sm" fw={500}>
              Select Recipients
            </Text>
            {!loadingRecipients && (
              <Group gap="xs">
                <UnstyledButton onClick={selectAll}>
                  <Text size="xs" c="blue" fw={500}>
                    Select All
                  </Text>
                </UnstyledButton>
                <Text size="xs" c="dimmed">
                  |
                </Text>
                <UnstyledButton onClick={deselectAll}>
                  <Text size="xs" c="blue" fw={500}>
                    Deselect All
                  </Text>
                </UnstyledButton>
              </Group>
            )}
          </Group>

          {loadingRecipients ? (
            <Group justify="center" py="md">
              <Loader size="sm" />
              <Text size="sm" c="dimmed">
                Loading participants...
              </Text>
            </Group>
          ) : (
            <Paper withBorder p={0}>
              <ScrollArea.Autosize mah={220}>
                <Stack gap={0}>
                  {eligibleRecipients.map((recipient) => (
                    <UnstyledButton
                      key={recipient.id}
                      onClick={() => toggleParticipant(recipient.id)}
                      px="sm"
                      py={8}
                      style={(theme) => ({
                        borderBottom: `1px solid ${theme.colors.gray[2]}`,
                        '&:last-child': { borderBottom: 'none' },
                      })}
                    >
                      <Group gap="sm" wrap="nowrap">
                        <Checkbox
                          checked={selectedIds.has(recipient.id)}
                          onChange={() => {}}
                          tabIndex={-1}
                          styles={{ input: { cursor: 'pointer', pointerEvents: 'none' } }}
                        />
                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <Group gap="xs" wrap="nowrap">
                            <Text size="sm" truncate>
                              {recipient.first_name} {recipient.last_name}
                            </Text>
                            <Text size="xs" c="dimmed" truncate>
                              {recipient.email}
                            </Text>
                          </Group>
                        </Box>
                        {recipient.intake_status === 'not_configured' && (
                          <Badge size="xs" variant="light" color="blue">
                            New
                          </Badge>
                        )}
                        {recipient.intake_reminder_count >= 3 && (
                          <Badge size="xs" variant="light" color="orange">
                            Max reminders
                          </Badge>
                        )}
                      </Group>
                    </UnstyledButton>
                  ))}
                  {completedRecipients.map((recipient) => (
                    <Box
                      key={recipient.id}
                      px="sm"
                      py={8}
                      style={(theme) => ({
                        borderBottom: `1px solid ${theme.colors.gray[2]}`,
                        opacity: 0.5,
                        '&:last-child': { borderBottom: 'none' },
                      })}
                    >
                      <Group gap="sm" wrap="nowrap">
                        <Checkbox checked={false} disabled />
                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <Group gap="xs" wrap="nowrap">
                            <Text size="sm" truncate>
                              {recipient.first_name} {recipient.last_name}
                            </Text>
                            <Text size="xs" c="dimmed" truncate>
                              {recipient.email}
                            </Text>
                          </Group>
                        </Box>
                        <Text size="xs" c="dimmed">
                          (Completed)
                        </Text>
                      </Group>
                    </Box>
                  ))}
                </Stack>
              </ScrollArea.Autosize>
              <Box
                px="sm"
                py={6}
                style={(theme) => ({
                  borderTop: `1px solid ${theme.colors.gray[2]}`,
                  backgroundColor: theme.colors.gray[0],
                })}
              >
                <Text size="xs" c="dimmed">
                  {selectedCount} of {eligibleRecipients.length} eligible participant
                  {eligibleRecipients.length !== 1 ? 's' : ''} selected
                </Text>
              </Box>
            </Paper>
          )}
        </Box>

        {/* First-time send callout (MED-345) */}
        {firstTimeSelected.length > 0 && (
          <Alert color="blue" icon={<Info size={16} />} variant="light">
            <Text size="sm" fw={500} mb={4}>
              {firstTimeSelected.length} participant{firstTimeSelected.length !== 1 ? 's' : ''} will
              receive the intake form for the first time:
            </Text>
            <Stack gap={2}>
              {firstTimeSelected.map((r) => (
                <Text key={r.id} size="sm">
                  &bull; {r.first_name} {r.last_name}
                </Text>
              ))}
            </Stack>
          </Alert>
        )}

        {/* Merge Tag Buttons */}
        <Box>
          <Group gap="xs" mb={8}>
            <Tag size={14} />
            <Text size="sm" fw={500}>
              Insert Merge Tag
            </Text>
          </Group>
          <Group gap="xs">
            {MERGE_TAGS.map(({ tag, label }) => (
              <Tooltip key={tag} label={`Insert ${tag}`}>
                <Badge
                  variant="light"
                  color="gray"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleInsertTag(tag)}
                >
                  {label}
                </Badge>
              </Tooltip>
            ))}
          </Group>
        </Box>

        <TextInput
          ref={subjectField.ref}
          label="Subject"
          placeholder="Enter email subject"
          value={subject}
          onChange={(e) => setSubject(e.currentTarget.value)}
          onFocus={() => setLastFocusedField('subject')}
          onSelect={subjectField.trackCursor}
        />

        <Textarea
          ref={bodyField.ref}
          label="Message Body"
          placeholder="Enter email message"
          value={body}
          onChange={(e) => setBody(e.currentTarget.value)}
          onFocus={() => setLastFocusedField('body')}
          onSelect={bodyField.trackCursor}
          minRows={8}
          autosize
          maxRows={12}
        />

        {/* Preview Toggle */}
        <Group justify="flex-end">
          <Button
            variant="subtle"
            size="xs"
            leftSection={<Eye size={14} />}
            onClick={() => setShowPreview(!showPreview)}
          >
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </Button>
        </Group>

        {/* Preview Section */}
        {showPreview && (
          <Paper withBorder p="md" bg="gray.0">
            <Stack gap="xs">
              <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                Preview (with sample data)
              </Text>
              <Divider />
              <Box>
                <Text size="sm" fw={500}>
                  Subject:
                </Text>
                <Text size="sm">{getPreviewText(subject)}</Text>
              </Box>
              <Box>
                <Text size="sm" fw={500}>
                  Body:
                </Text>
                {/* The intake email template renders a "Hi {first_name}," greeting above the
                    body — prepend it here so the preview matches the rendered email. */}
                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                  {getPreviewText(`Hi {{first_name}},\n\n${body}`)}
                </Text>
              </Box>
            </Stack>
          </Paper>
        )}

        <Divider />

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => setConfirmOpened(true)}
            loading={sending}
            disabled={selectedCount === 0}
            color="warningAmber"
          >
            Send to {selectedCount} Participant{selectedCount !== 1 ? 's' : ''}
          </Button>
        </Group>
      </Stack>

      {/* MED-646: Bulk send confirmation dialog */}
      <Modal
        opened={confirmOpened}
        onClose={() => setConfirmOpened(false)}
        title="Confirm Email Send"
        size="sm"
        centered
        zIndex={301}
      >
        <Stack gap="md">
          <Alert icon={<AlertTriangle size={16} />} color="yellow" variant="light">
            <Text size="sm">
              You are about to send this email to{' '}
              <Text component="span" fw={700}>
                {selectedCount} participant{selectedCount !== 1 ? 's' : ''}
              </Text>
              . This action cannot be undone.
            </Text>
          </Alert>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => setConfirmOpened(false)}
              disabled={sending}
            >
              Cancel
            </Button>
            <Button
              color="medleyGreen"
              onClick={() => {
                setConfirmOpened(false);
                handleSend();
              }}
              loading={sending}
              disabled={sending}
            >
              Confirm &amp; Send
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Modal>
  );
}
